<template>
  <div>
    <div class="page-header flex-shrink-0">
      <div class="container-fluid d-flex justify-content-between px-md-4 px-3">
        <h3 class="my-2">Insurance Claims &nbsp;<b-spinner v-if="processing" small label="Small Spinner"></b-spinner></h3>

        <div>
          <div style="display:inline-block">
            <b-input-group class="flex-nowrap">
              <date-picker style="cursor: pointer"
                @input="onSelectDateRange"
                @clear="onClearDateRange"
                :open.sync="datePickerOpen"
                v-model="selectedDateRange"
                :formatter="momentFormat"
                :clearable="true"
                :editable="false"
                input-class="form-control bg-white"
                placeholder="Select a date range"
                range>
                <template v-slot:footer="{ emit }">
                  <div class="d-flex justify-content-between">
                    <button class="mx-btn mx-btn-text" @click="onSelect90DayDateRange(emit);">Last 90 days <i class="fas fa-arrow-circle-right"></i></button>
                    <button class="mx-btn mx-btn-text" @click="onSelectMinMaxDateRange(emit);">All Claims: {{minMaxDateRange.min | formatDateNoTime}} - {{minMaxDateRange.max | formatDateNoTime}} <i class="fas fa-arrow-circle-right"></i></button>
                  </div>
                </template>
              </date-picker>
              <!--
              <b-input-group-append>
                <b-button variant="success" @click="onSelectDateRange" :disabled="selectedDateRange[0] == null">Go</b-button>
              </b-input-group-append>
              -->
            </b-input-group>
          </div>
          <b-tooltip target="info" non-interactive no-fade>Claims exist within the <br />following date range: <br />{{minMaxDateRange.min | formatDateNoTime}} - {{minMaxDateRange.max | formatDateNoTime}}</b-tooltip>
          <div id="info" style="display:inline-block">
            <b-icon-question-circle-fill></b-icon-question-circle-fill>
          </div>
        </div>
        <div>
          <b-button variant="success" @click="doDownloadCsv" :disabled="claims.length === 0">Download as CSV</b-button>
        </div><!-- EMPTY NODE FOR ALIGNMENT-->
      </div>
      <div class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3 py-2">
        <div class="alert alert-info" v-show="!processing && validRangeSelected && claims.length == 0">There are no claims matching the chosen date range!</div>
        <div class="alert alert-info" v-show="!processing && !validRangeSelected && claims.length == 0">Select a date range to search for claims</div>
        <b-table-simple bordered class="mb-5 rg-table" v-show="!processing && claims.length > 0">
          <b-thead>
              <b-tr>
                  <b-th>Title</b-th>
                  <b-th>Event Number</b-th>
                  <b-th>Date of Incident</b-th>
                  <b-th>Property</b-th>
                  <b-th>Type</b-th>
                  <b-th>Category</b-th>
                  <b-th>Claim Type</b-th>
                  <b-th>Date of Claim</b-th>
                  <b-th>Insurance Co.</b-th>
                  <b-th>Adjuster Name</b-th>
                  <b-th>Policy Number</b-th>
                  <b-th>Claim Number</b-th>
                  <b-th>Deductible</b-th>
                  <b-th>Adjustment</b-th>
                  <b-th>Cost Estimate</b-th>
                  <b-th>Current Status</b-th>
              </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(claim) in claims" v-bind:key="claim.id">
              <b-td>
                {{ claim.title }}
              </b-td>
              <b-td>
                {{ claim.eventNumber }}
              </b-td>
              <b-td>
                {{ claim.dateOccurredUtc | formatDateNoTime }}
              </b-td>
              <b-td>
                {{ claim.propertyName }}
              </b-td>
              <b-td>
                {{ claim.type.name  }}
              </b-td>
              <b-td>
                {{ claim.category.name  }}
              </b-td>
              <b-td>
                {{ claim.claimTypeString  }}
              </b-td>
              <b-td>
                {{ claim.claimDate | formatDateNoTime }}
              </b-td>
              <b-td>
                {{ claim.insuranceCompany || '--' }}
              </b-td>
              <b-td>
                {{ claim.adjusterName || '--' }}
              </b-td>
              <b-td>
                {{ claim.policyNumber || '--' }}
              </b-td>
              <b-td>
                {{ claim.claimNumber || '--' }}
              </b-td>
              <b-td>
                {{ claim.deductible || '--' }}
              </b-td>
              <b-td>
                {{ claim.adjustment || '--' }}
              </b-td>
              <b-td>
                {{ claim.costEstimate || '--' }}
              </b-td>
              <b-td>
                {{ claim.status || 'NOT YET AVAILABLE' }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: 'InsuranceClaims',
  components: {DatePicker},
  data () {
    return {
      dateFormat: 'MM/DD/YYYY',
      selectedDateRange: [null, null],
      minMaxDateRange: {min: null, max: null},
      // Date picker specifics
      momentFormat: {
        stringify: (date) => {
          // console.log('stringify: ', date)
          return date ? `${moment(date).format(this.dateFormat)}` : 'ddd'
        },
        parse: (value) => {
          // console.log('value: ', value)
          return value ? moment(value, this.dateFormat).toDate() : null
        }
      },
      datePickerOpen: false,
      claims: [],
      processing: false
    }
  },
  computed: {
    validRangeSelected () {
      let valid = this.selectedDateRange !== null &&
      this.selectedDateRange.length === 2 &&
      this.selectedDateRange[0] !== null &&
      this.selectedDateRange[1] !== null
      return valid
    }
  },
  async created () {
    this.processing = true
    await this.doGetClaimsDateRange()
    // await this.doGetReport()
    this.processing = false
  },
  methods: {
    ...mapActions('reports', ['getInsuranceClaimReport', 'getInsuranceClaimDateRange']),
    async doGetReport () {
      this.processing = true
      await this.getInsuranceClaimReport({claimDateOnOrBefore: this.selectedDateRange[1], claimDateOnOrAfter: this.selectedDateRange[0]}).then((r) => {
        this.claims = r.data.insuranceClaimsWithEvents
      })
      this.processing = false
    },
    async doGetClaimsDateRange () {
      this.getInsuranceClaimDateRange().then((r) => {
        this.minMaxDateRange.min = r.data.item1
        this.minMaxDateRange.max = r.data.item2
      })
    },
    async onSelectDateRange (range) {
      this.selectedDateRange = range
      if (this.selectedDateRange[0] !== null && this.selectedDateRange[1] !== null) {
        await this.doGetReport()
      }
    },
    async onSelect90DayDateRange () {
      this.selectedDateRange = [moment().add(-3, 'month').toDate(), moment().toDate()]
      this.datePickerOpen = false

      await this.doGetReport()
    },
    async onSelectMinMaxDateRange () {
      this.selectedDateRange = [moment(this.minMaxDateRange.min).toDate(), moment(this.minMaxDateRange.max).toDate()]
      this.datePickerOpen = false

      await this.doGetReport()
    },
    async onClearDateRange () {
      this.claims = []
    },
    disabledDateRange (date) {
      // Currently not being used
    },
    download (csvData) {
      // Creating a Blob for having a csv file format
    // and passing the data with type
      const blob = new Blob([csvData], { type: 'text/csv' })

      // Creating an object for downloading url
      const url = window.URL.createObjectURL(blob)

      // Creating an anchor(a) tag of HTML
      const a = document.createElement('a')

      // Passing the blob downloading url
      a.setAttribute('href', url)

      // Setting the anchor tag attribute for downloading
      // and passing the download file name
      a.setAttribute('download', 'download.csv')

      // Performing a download with click
      a.click()
    },
    claimsToCsv (data) {
      const keys = [
        'Title', 'Event Number', 'Date of Incident', 'Property',
        'Type', 'Category', 'Claim Type', 'Date of Claim',
        'Insurance Co.', 'Adjuster Name', 'Policy Number', 'Claim Number',
        'Deductible', 'Adjustment', 'Cost Estimate', 'Current Status'
      ]

      var csvStr = `${keys.join()}\n`

      data.forEach(e => {
        var values = [
          e.title, e.eventNumber, moment.utc(e.dateOccurredUtc).format('MM/DD/YYYY'), e.propertyName,
          e.type.name, e.category.name, e.claimTypeString, moment.utc((e.claimDate)).format('MM/DD/YYYY'),
          (e.insuranceCompany || ' '), (e.adjusterName || ' '), (e.policyNumber || ' '), (e.claimNumber || ' '),
          (e.deductible || ' '), (e.adjustment || ' '), (e.costEstimate || ' '), (e.status || ' ')
        ].map((val) => {
          if (typeof val === 'string') {
            return val.replace(val, `"${val}"`)
          } else {
            return val
          }
        })
        csvStr = `${csvStr}${values.join()}\n`
      })

      return csvStr
    },
    doDownloadCsv () {
      const csvdata = this.claimsToCsv(this.claims)
      this.download(csvdata)
    }
  }
}
</script>
<style>
  .sticky-header {
    height: 500px;
  }
</style>



// WEBPACK FOOTER //
// src/views/reports/InsuranceClaimReport.vue